import React, { useState, useEffect } from 'react';
import { Card, Typography, Space, Row, Col, Tag, Button, Modal, Input, message, Popconfirm, Empty, Spin, Tabs, Divider, Segmented, Switch, Checkbox } from 'antd';
import { BookOutlined, EditOutlined, DeleteOutlined, EyeOutlined, ReloadOutlined, BarChartOutlined, DashboardOutlined, HeatMapOutlined, LineChartOutlined, SearchOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { formatTimestamp } from '../../utils/timeUtils';
import { useTranslation } from 'react-i18next';
import Dashboard from '../Analysis/Dashboard';
import HeatmapTable from '../Analysis/HeatmapTable';
import AverageReturnTable from '../Analysis/AverageReturnTable';
import { getConfig, getConfigAsync } from '../../utils/config';
import { useAppTheme } from '../../theme/ThemeProvider';
import { getUserIdentityForApi } from '../../utils/auth-aws-sdk';
import { logger } from '../../utils/logger';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * Slim down analysis_result before sending to avoid 413 Content Too Large.
 * API Gateway has a 10MB limit; analysisResult can exceed this when it contains
 * segments_with_returns, full feature vectors in similar_points, etc.
 * Backend prepare_analysis_data only needs minimal fields.
 */
function slimAnalysisResultForSave(analysisResult: any): any {
  if (!analysisResult) return analysisResult;

  const slim: any = {};

  // Point signal: similar_points - backend only needs datetime + similarity (filters by >= 0.8)
  if (Array.isArray(analysisResult.similar_points)) {
    slim.similar_points = analysisResult.similar_points.map((p: any) => ({
      datetime: p.datetime,
      similarity: p.similarity ?? (typeof p.distance === 'number' ? 1 - p.distance : p.similarity)
    }));
  }

  // target_point - backend only needs datetime
  if (analysisResult.target_point) {
    slim.target_point = {
      datetime: analysisResult.target_point.datetime
    };
  }

  // scaler_params - needed for realtime similarity, keep as-is (typically small)
  if (analysisResult.scaler_params) {
    slim.scaler_params = analysisResult.scaler_params;
  }

  // Rectangle signal: similarity_analysis - slim similar_segments
  if (analysisResult.similarity_analysis) {
    const sa = analysisResult.similarity_analysis;
    slim.similarity_analysis = {
      total_found: sa.total_found,
      target_segment: sa.target_segment
    };
    if (Array.isArray(sa.similar_segments)) {
      slim.similarity_analysis.similar_segments = sa.similar_segments.map((seg: any) => {
        const out: any = {
          start_time: seg.start_time,
          end_time: seg.end_time,
          correlation: seg.correlation
        };
        if (seg.technical_analysis) {
          const ta = seg.technical_analysis;
          out.technical_analysis = {
            trend_analysis: ta.trend_analysis ? { slope: ta.trend_analysis.slope, is_converging: ta.trend_analysis.is_converging } : undefined,
            volatility_analysis: ta.volatility_analysis ? { volatility: ta.volatility_analysis.volatility } : undefined,
            volume_analysis: ta.volume_analysis ? { volume_change: ta.volume_analysis.volume_change } : undefined,
            momentum_analysis: ta.momentum_analysis ? { rsi_current: ta.momentum_analysis.rsi_current } : undefined
          };
        }
        return out;
      });
    }
  }

  // returns_analysis - remove segments_with_returns everywhere (main payload bloat)
  const stripSegmentsWithReturns = (obj: any): any => {
    if (obj == null) return obj;
    if (Array.isArray(obj)) return obj.map(stripSegmentsWithReturns);
    if (typeof obj === 'object') {
      const out: any = {};
      for (const [k, v] of Object.entries(obj)) {
        if (k === 'segments_with_returns') continue;
        out[k] = stripSegmentsWithReturns(v);
      }
      return out;
    }
    return obj;
  };

  if (analysisResult.returns_analysis) {
    slim.returns_analysis = stripSegmentsWithReturns(analysisResult.returns_analysis);
  }

  return slim;
}

interface SavedSignal {
  id: string;
  name: string;
  symbol: string;
  timeframe: string;
  signal_type?: 'rectangle' | 'point';
  time_range: {
    start_time: string;
    end_time: string;
  };
  analysis_data: any;
  created_at: string;
  updated_at: string;
  description?: string;
}

interface SignalManagerProps {
  onSignalSelect?: (signal: SavedSignal) => void;
  onAnalysisResultSelect?: (analysisResult: any) => void;
  pendingSaveData?: {
    analysisResult: any,
    timeRange: { start_time: string; end_time: string },
    selectedRectangle?: any,
    selectedPoint?: { time: number; price: number },
    symbol?: string,
    timeframe?: string
  } | null;
  onSaveComplete?: () => void;
}

const SignalManager: React.FC<SignalManagerProps> = ({
  onSignalSelect,
  onAnalysisResultSelect,
  pendingSaveData,
  onSaveComplete
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useAppTheme();
  const [signals, setSignals] = useState<SavedSignal[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [viewingSignal, setViewingSignal] = useState<SavedSignal | null>(null);
  const [editingSignal, setEditingSignal] = useState<SavedSignal | null>(null);
  const [signalName, setSignalName] = useState('');
  const [signalDescription, setSignalDescription] = useState('');
  const [analysisViewMode, setAnalysisViewMode] = useState<'all' | 'discovery' | 'validation'>('all');

  // Alert config (MVP: store with signal; frontend notification UI comes later)
  const [enableAlert, setEnableAlert] = useState(false);
  const [alertInApp, setAlertInApp] = useState(true);
  const [alertEmailEnabled, setAlertEmailEnabled] = useState(false);
  const [alertEmail, setAlertEmail] = useState('');

  // 加载保存的信号
  const loadSignals = async () => {
    setLoading(true);
    try {
      const config = await getConfigAsync();
      const API_BASE_URL = config.API_BASE_URL;
      const { getApiHeadersWithoutContentType } = await import('../../utils/apiHeaders');
      const response = await fetch(`${API_BASE_URL}/signal/list`, {
        headers: getApiHeadersWithoutContentType()
      });
      const data = await response.json();
      if (data.success) {
        // 处理信号数据，确保symbol字段正确
        // 检查后端返回的数据结构，处理可能的字段映射问题
        const processedSignals = data.signals.map((signal: any) => {
          // 检查是否有ticker字段（某些API可能使用ticker而不是symbol）
          // 也检查所有可能的字段名变体
          const symbolValue = signal.ticker || signal.symbol || signal.Symbol || signal.TICKER || signal.ticker_symbol;
          
          // 如果找到了有效的symbol值且不是默认的btcusdt，使用它
          if (symbolValue && symbolValue.toLowerCase() !== 'btcusdt' && symbolValue.toLowerCase() !== 'btc/usdt') {
            return {
              ...signal,
              symbol: symbolValue
            };
          }
          
          // 如果symbol是btcusdt或空，尝试从analysis_data中获取
          // 某些情况下，symbol信息可能存储在analysis_data中
          if ((!symbolValue || symbolValue.toLowerCase() === 'btcusdt' || symbolValue.toLowerCase() === 'btc/usdt') && signal.analysis_data) {
            // 尝试从analysis_data中查找symbol信息
            const analysisDataStr = JSON.stringify(signal.analysis_data);
            // 查找可能的symbol模式（如ETHUSDT, SOLUSDT等）
            const symbolMatch = analysisDataStr.match(/"symbol":\s*"([^"]+)"/i) || 
                               analysisDataStr.match(/"ticker":\s*"([^"]+)"/i) ||
                               analysisDataStr.match(/([A-Z]{2,10}USDT)/);
            
            if (symbolMatch && symbolMatch[1] && symbolMatch[1].toLowerCase() !== 'btcusdt') {
              logger.log(`从analysis_data中提取symbol: ${symbolMatch[1]} (信号ID: ${signal.id})`);
              return {
                ...signal,
                symbol: symbolMatch[1]
              };
            }
          }
          
          // 如果仍然没有找到有效的symbol，记录警告
          if (!symbolValue || symbolValue.toLowerCase() === 'btcusdt' || symbolValue.toLowerCase() === 'btc/usdt') {
            logger.warn(`信号 ${signal.id} (${signal.name}) 的symbol字段可能不正确: ${symbolValue || 'undefined'}`);
          }
          
          return signal;
        });
        setSignals(processedSignals);
      } else {
        message.error(t('signal.loadFailed'));
      }
    } catch (error) {
      logger.error('加载信号失败:', error);
      message.error(t('signal.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载信号
  useEffect(() => {
    loadSignals();
  }, []);

  // 处理待保存的数据
  useEffect(() => {
    if (pendingSaveData) {
      // 自动打开保存对话框
      setShowSaveModal(true);
      setSignalName(i18n.language && i18n.language.toLowerCase().startsWith('zh') ? '新信号' : 'New signal');
      setSignalDescription('');
      // Reset alert config on open
      setEnableAlert(false);
      setAlertInApp(true);
      setAlertEmailEnabled(false);
      setAlertEmail('');
    }
  }, [pendingSaveData, i18n.language]);

  const handleDeleteSignal = async (signalId: string) => {
    try {
      const config = await getConfigAsync();
      const API_BASE_URL = config.API_BASE_URL;
      const { getApiHeadersWithoutContentType } = await import('../../utils/apiHeaders');
      const response = await fetch(`${API_BASE_URL}/signal/${signalId}`, {
        method: 'DELETE',
        headers: getApiHeadersWithoutContentType()
      });

      const data = await response.json();
      if (data.success) {
        message.success(t('signal.deleteSuccess'));
        loadSignals(); // 重新加载信号列表
      } else {
        message.error(data.error || t('signal.deleteFailed'));
      }
    } catch (error) {
      message.error(t('signal.deleteFailed'));
      logger.error('删除信号错误:', error);
    }
  };

  const handleEditSignal = (signal: SavedSignal) => {
    setEditingSignal(signal);
    setSignalName(signal.name);
    setSignalDescription(signal.description || '');
    setShowEditModal(true);
  };

  const confirmSaveSignal = async () => {
    if (!signalName.trim()) {
      message.error('请输入信号名称');
      return;
    }

    if (!pendingSaveData) return;

    // Validate alert config if enabled
    if (enableAlert) {
      if (!alertInApp && !alertEmailEnabled) {
        message.error(i18n.language && i18n.language.toLowerCase().startsWith('zh')
          ? '请选择至少一种提醒方式（应用内或邮件）'
          : 'Please select at least one alert channel (In-App or Email).'
        );
        return;
      }
    }

    try {
      const config = await getConfigAsync();
      const API_BASE_URL = config.API_BASE_URL;
      const { getApiHeaders } = await import('../../utils/apiHeaders');

      const channels: string[] = [];
      if (enableAlert && alertInApp) channels.push('in_app');
      if (enableAlert && alertEmailEnabled) channels.push('email');

      // Resolve registered email from Cognito id token (if user leaves email empty)
      const identity = getUserIdentityForApi();
      const registeredEmail = (identity?.email || '').trim();
      const resolvedEmail = (alertEmailEnabled ? (alertEmail.trim() || registeredEmail) : '');

      const alert_config = enableAlert ? {
        enabled: true,
        channels,
        // If empty, backend can still treat as "use registered email", but we prefer
        // to send the resolved registered email when available for easier debugging.
        email: (alertEmailEnabled && resolvedEmail) ? resolvedEmail : undefined
      } : undefined;

      // Debug log: which email will be used (user-entered vs default registered email)
      if (enableAlert && alertEmailEnabled) {
        const emailToUse = resolvedEmail || '(no email in token)';
        // eslint-disable-next-line no-console
        console.log('[SaveSignal] alert email:', emailToUse);
      }

      const slimmedResult = slimAnalysisResultForSave(pendingSaveData.analysisResult);

      // 确保symbol字段正确传递
      const symbolToSave = pendingSaveData.symbol || 'BTCUSDT';
      
      // 调试日志：记录保存时的symbol值
      if (process.env.NODE_ENV === 'development') {
        logger.log('[SaveSignal] 保存信号时的symbol值:', {
          pendingSaveDataSymbol: pendingSaveData.symbol,
          symbolToSave: symbolToSave,
          pendingSaveData: pendingSaveData
        });
      }

      const response = await fetch(`${API_BASE_URL}/signal/save`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          name: signalName,
          description: signalDescription,
          symbol: symbolToSave,
          timeframe: pendingSaveData.timeframe || '1h',
          time_range: pendingSaveData.timeRange,
          strategy_type: pendingSaveData.selectedPoint ? 'point' : 'rectangle',  // 保持向后兼容
          analysis_result: slimmedResult,
          alert_config
        })
      });

      const data = await response.json();
      if (data.success) {
        setShowSaveModal(false);
        setSignalName('');
        setSignalDescription('');
        setEnableAlert(false);
        setAlertInApp(true);
        setAlertEmailEnabled(false);
        setAlertEmail('');
        message.success(t('signal.saveSuccess'));
        loadSignals(); // 重新加载信号列表
        onSaveComplete?.(); // 通知父组件保存完成
      } else {
        message.error(data.error || t('signal.saveFailed'));
      }
    } catch (error) {
      message.error(t('signal.saveFailed'));
      logger.error('保存信号错误:', error);
    }
  };

  const confirmEditSignal = async () => {
    if (!signalName.trim()) {
      message.error(t('signal.name') + '不能为空');
      return;
    }

    if (!editingSignal) return;

    try {
      const config = await getConfigAsync();
      const API_BASE_URL = config.API_BASE_URL;
      const { getApiHeaders } = await import('../../utils/apiHeaders');
      const response = await fetch(`${API_BASE_URL}/signal/${editingSignal.id}`, {
        method: 'PUT',
        headers: getApiHeaders(),
        body: JSON.stringify({
          name: signalName,
          description: signalDescription
        })
      });

      const data = await response.json();
      if (data.success) {
        setShowEditModal(false);
        setEditingSignal(null);
        setSignalName('');
        setSignalDescription('');
        message.success(t('signal.editSuccess'));
        loadSignals(); // 重新加载信号列表
      } else {
        message.error(data.error || t('signal.editFailed'));
      }
    } catch (error) {
      message.error(t('signal.editFailed'));
      logger.error('更新信号错误:', error);
    }
  };

  const handleViewSignal = (signal: SavedSignal) => {
    setViewingSignal(signal);
    setShowAnalysisModal(true);
  };

  const renderAnalysisModal = () => {
    if (!viewingSignal) return null;

    // 获取原始分析数据
    const rawReturnsAnalysis = viewingSignal.analysis_data?.returns_analysis || null;
    
    // 提取 discovery 和 validation 数据
    const discoveryData = rawReturnsAnalysis?.discovery;
    const validationData = rawReturnsAnalysis?.validation;
    
    const hasDiscovery = discoveryData?.returns_analysis && discoveryData.sample_count > 0;
    const hasValidation = validationData?.returns_analysis && validationData.sample_count > 0;
    
    // 根据 viewMode 构建要显示的数据
    const getDisplayData = () => {
      if (analysisViewMode === 'discovery' && hasDiscovery) {
        return { returns_analysis: discoveryData.returns_analysis };
      }
      if (analysisViewMode === 'validation' && hasValidation) {
        return { returns_analysis: validationData.returns_analysis };
      }
      // all 模式：显示合并的数据（排除 discovery/validation 键）
      if (rawReturnsAnalysis) {
        const { discovery, validation, ...restData } = rawReturnsAnalysis;
        return { returns_analysis: restData };
      }
      return { returns_analysis: null };
    };
    
    const analysisData = getDisplayData();

    return (
      <Modal
        title={
          <Space>
            <DashboardOutlined />
            {t('signal.analysisDetails')} - {viewingSignal.name}
          </Space>
        }
        open={showAnalysisModal}
        onCancel={() => {
          setShowAnalysisModal(false);
          setViewingSignal(null);
          setAnalysisViewMode('all');
        }}
        footer={[
          <Button key="close" onClick={() => {
            setShowAnalysisModal(false);
            setViewingSignal(null);
            setAnalysisViewMode('all');
          }}>
            {t('common.close')}
          </Button>
        ]}
        width={1200}
        style={{ top: 20 }}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* 信号基本信息 */}
          <Card size="small" style={{ marginBottom: 16, background: theme.surfaceBackground, borderColor: theme.surfaceBorder }}>
                         <Row gutter={[16, 8]}>
               <Col span={8}>
                 <Text type="secondary">{t('signal.symbol')}:</Text>
                 <div>{viewingSignal.symbol}</div>
               </Col>
               <Col span={8}>
                 <Text type="secondary">{t('signal.timeframe')}:</Text>
                 <div>{viewingSignal.timeframe}</div>
               </Col>
               <Col span={8}>
                 <Text type="secondary">{t('signal.timeRange')}:</Text>
                 <div>{formatTimestamp(viewingSignal.time_range.start_time)}</div>
                 <div>~</div>
                 <div>{formatTimestamp(viewingSignal.time_range.end_time)}</div>
               </Col>
              
            </Row>
            {viewingSignal.description && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">{t('signal.description')}:</Text>
                <div style={{ marginTop: 4 }}>{viewingSignal.description}</div>
              </div>
            )}
          </Card>

          {/* 分析结果展示 */}
          {analysisData.returns_analysis ? (
            <>
              {/* Before/After 切换控件 */}
              {(hasDiscovery || hasValidation) && (
                <div style={{ 
                  marginBottom: 16, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center'
                }}>
                  <Segmented
                    value={analysisViewMode}
                    onChange={(value) => setAnalysisViewMode(value as 'all' | 'discovery' | 'validation')}
                    options={[
                      {
                        label: (
                          <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>📊</span>
                            <span>{t('analysis.split.all', 'All')}</span>
                          </div>
                        ),
                        value: 'all',
                      },
                      {
                        label: (
                          <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <SearchOutlined style={{ color: '#1890ff' }} />
                            <span style={{ color: hasDiscovery ? undefined : '#999' }}>
                              {t('analysis.split.discovery', 'Before')}
                            </span>
                            {hasDiscovery && (
                              <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>
                                {discoveryData?.sample_count}
                              </Tag>
                            )}
                          </div>
                        ),
                        value: 'discovery',
                        disabled: !hasDiscovery,
                      },
                      {
                        label: (
                          <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <span style={{ color: hasValidation ? undefined : '#999' }}>
                              {t('analysis.split.validation', 'After')}
                            </span>
                            {hasValidation && (
                              <Tag color="green" style={{ margin: 0, fontSize: 10 }}>
                                {validationData?.sample_count}
                              </Tag>
                            )}
                          </div>
                        ),
                        value: 'validation',
                        disabled: !hasValidation,
                      },
                    ]}
                    style={{ background: theme.toolbarBackground }}
                  />
                </div>
              )}
              
            <Tabs
              defaultActiveKey="dashboard"
              items={[
                {
                  key: "dashboard",
                  label: (
                    <Space>
                      <DashboardOutlined />
                      {t('analysis.performanceDashboard')}
                    </Space>
                  ),
                  children: (
                    <div style={{ padding: '16px 0' }}>
                      <Dashboard returnsAnalysis={analysisData} />
                    </div>
                  )
                },
                {
                  key: "heatmap",
                  label: (
                    <Space>
                      <HeatMapOutlined />
                      {t('analysis.winRateHeatmap')}
                    </Space>
                  ),
                  children: (
                    <div style={{ padding: '16px 0' }}>
                      <HeatmapTable returnsAnalysis={analysisData} />
                    </div>
                  )
                },
                {
                  key: "returns",
                  label: (
                    <Space>
                      <LineChartOutlined />
                      {t('analysis.averageReturnTable')}
                    </Space>
                  ),
                  children: (
                    <div style={{ padding: '16px 0' }}>
                      <AverageReturnTable returnsAnalysis={analysisData} />
                    </div>
                  )
                }
              ]}
            />
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: theme.textSecondary }}>
              <BarChartOutlined style={{ fontSize: 24, marginBottom: 8 }} />
              <div>{t('analysis.noAnalysisData')}</div>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  const renderSignalCard = (signal: SavedSignal) => (
    <Col span={6} key={signal.id}>
      <Card
        size="small"
        style={{ 
          marginBottom: 16, 
          background: theme.surfaceBackground,
          border: `1px solid ${theme.surfaceBorder}`,
          borderRadius: 8,
          transition: 'all 0.3s'
        }}
        hoverable
      >
        <Row align="middle" justify="space-between">
          <Col flex="auto">
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: '16px', color: theme.accentPrimary }}>
                {signal.name}
              </Text>
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {signal.symbol}
              </Tag>
              <Tag color="green" style={{ marginLeft: 4 }}>
                {signal.timeframe}
              </Tag>
            {signal.signal_type && (
              <Tag color={signal.signal_type === 'point' ? 'purple' : 'geekblue'} style={{ marginLeft: 4 }}>
                {signal.signal_type === 'point' ? 'Point' : 'Rectangle'}
              </Tag>
            )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px', color: theme.textSecondary }}>
                {t('signal.timeRange')}: {formatTimestamp(signal.time_range.start_time)} ~ {formatTimestamp(signal.time_range.end_time)}
              </Text>
            </div>
            {signal.description && (
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px', color: theme.textSecondary }}>
                {t('signal.description')}: {signal.description}
                </Text>
              </div>
            )}
            <div>
              <Text type="secondary" style={{ fontSize: '11px', color: theme.textSecondary }}>
                {t('signal.createdAt')}: {new Date(signal.created_at).toLocaleString()}
              </Text>
            </div>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewSignal(signal)}
              >
                {t('signal.view')}
              </Button>
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditSignal(signal)}
              >
                {t('signal.edit')}
              </Button>
              <Popconfirm
                title={t('signal.confirmDelete')}
                description={t('signal.deleteWarning')}
                onConfirm={() => handleDeleteSignal(signal.id)}
                okText={t('common.confirm')}
                cancelText={t('common.cancel')}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  danger
                >
                  {t('signal.delete')}
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      </Card>
    </Col>
  );

  const renderEditModal = () => (
    <Modal
      title={t('signal.edit') + t('signal.name')}
      open={showEditModal}
      onOk={confirmEditSignal}
      onCancel={() => {
        setShowEditModal(false);
        setEditingSignal(null);
        setSignalName('');
        setSignalDescription('');
      }}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>{t('signal.name')} *</Text>
        <Input
          placeholder={t('signal.namePlaceholder')}
          value={signalName}
          onChange={(e) => setSignalName(e.target.value)}
          style={{ marginTop: 8 }}
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <Text strong>{t('signal.description')}</Text>
        <TextArea
          placeholder={t('signal.descriptionOptionalPlaceholder')}
          value={signalDescription}
          onChange={(e) => setSignalDescription(e.target.value)}
          rows={3}
          style={{ marginTop: 8 }}
        />
      </div>
      <div style={{ background: theme.toolbarBackground, padding: 12, borderRadius: 6, border: `1px solid ${theme.surfaceBorder}` }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {t('signal.timeRange')}：{editingSignal ? `${formatTimestamp(editingSignal.time_range.start_time)} ~ ${formatTimestamp(editingSignal.time_range.end_time)}` : ''}
        </Text>
      </div>
    </Modal>
  );

  const renderSaveModal = () => (
    <Modal
      title={t('signal.saveSignal')}
      open={showSaveModal}
      onOk={confirmSaveSignal}
      onCancel={() => {
        setShowSaveModal(false);
        setSignalName('');
        setSignalDescription('');
        setEnableAlert(false);
        setAlertInApp(true);
        setAlertEmailEnabled(false);
        setAlertEmail('');
        onSaveComplete?.();
      }}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>{t('signal.name')} *</Text>
        <Input
          placeholder={t('signal.namePlaceholder')}
          value={signalName}
          onChange={(e) => setSignalName(e.target.value)}
          style={{ marginTop: 8 }}
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <Text strong>{t('signal.description')}</Text>
        <TextArea
          placeholder={t('signal.descriptionOptionalPlaceholder')}
          value={signalDescription}
          onChange={(e) => setSignalDescription(e.target.value)}
          rows={3}
          style={{ marginTop: 8 }}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />
      <div style={{ background: theme.toolbarBackground, padding: 12, borderRadius: 6, border: `1px solid ${theme.surfaceBorder}`, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: enableAlert ? 10 : 0 }}>
          <Text strong style={{ fontSize: 13 }}>
            {i18n.language && i18n.language.toLowerCase().startsWith('zh') ? '设置提醒' : 'Set Alert'}
          </Text>
          <Switch size="small" checked={enableAlert} onChange={setEnableAlert} />
        </div>

        {enableAlert && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Checkbox checked={alertInApp} onChange={(e) => setAlertInApp(e.target.checked)}>
                {i18n.language && i18n.language.toLowerCase().startsWith('zh') ? '应用内' : 'In-App'}
              </Checkbox>
              <Checkbox checked={alertEmailEnabled} onChange={(e) => setAlertEmailEnabled(e.target.checked)}>
                {i18n.language && i18n.language.toLowerCase().startsWith('zh') ? '邮件' : 'Email'}
              </Checkbox>
            </div>

            {alertEmailEnabled && (
              <Input
                value={alertEmail}
                onChange={(e) => setAlertEmail(e.target.value)}
                placeholder={i18n.language && i18n.language.toLowerCase().startsWith('zh')
                  ? '邮箱（留空使用注册邮箱）'
                  : 'Email (leave empty to use registered email)'
                }
              />
            )}

            <Text type="secondary" style={{ fontSize: 12 }}>
              {i18n.language && i18n.language.toLowerCase().startsWith('zh')
                ? '当新K线收盘后相似度≥80%时会生成一条提醒记录。'
                : 'A new alert record will be created when a candle closes and similarity ≥ 80%.'
              }
            </Text>
          </div>
        )}
      </div>

      <div style={{ background: theme.toolbarBackground, padding: 12, borderRadius: 6, border: `1px solid ${theme.surfaceBorder}` }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {t('signal.timeRange')}：{pendingSaveData ? `${formatTimestamp(pendingSaveData.timeRange.start_time)} ~ ${formatTimestamp(pendingSaveData.timeRange.end_time)}` : ''}
        </Text>
      </div>
    </Modal>
  );

  return (
    <div style={{ padding: '24px', background: theme.appBackground, minHeight: '100vh' }}>
      {/* 页面标题和操作按钮 */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            <Space>
              <BookOutlined />
              {t('signal.signalManagement')}
            </Space>
          </Title>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadSignals}
              loading={loading}
            >
              {t('common.refresh')}
            </Button>
          </Space>
        </div>
      </div>

      {/* 信号列表 */}
      <Spin spinning={loading}>
        {signals.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('signal.noSignals')}
            style={{ padding: '60px 0' }}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {signals.map((signal) => renderSignalCard(signal))}
          </Row>
        )}
      </Spin>

      {/* 编辑信号模态框 */}
      {renderEditModal()}

      {/* 保存信号模态框 */}
      {renderSaveModal()}

      {/* 分析结果展示模态框 */}
      {renderAnalysisModal()}
    </div>
  );
};

export default SignalManager;

